import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Head, useForm } from "@inertiajs/react";
import { Modal } from "@inertiaui/modal-react";
import { useState } from "react";
import type { Track, SimCar, SessionType, Simulator } from "@/types/generated";
import { PrivacySelect, PrivacyLevel } from "@/components/ui/privacy-select";

interface CreateEventProps {
  tracks: Track[];
  sim_cars: SimCar[];
  session_types: SessionType[];
  simulators: Simulator[];
  errors?: string[];
  form_data?: any;
}

export default function CreateEvent({
  tracks,
  sim_cars,
  session_types,
  simulators,
  errors = [],
  form_data = {},
}: CreateEventProps) {
  const {
    data,
    setData,
    post,
    processing,
    errors: formErrors,
  } = useForm({
    track_id: form_data.track_id || "",
    sim_car_id: form_data.sim_car_id || "",
    simulator: form_data.simulator || "",
    session_type: form_data.session_type || "",
    session_started_at: form_data.session_started_at || "",
    duration_seconds: form_data.duration_seconds || "",
    lap_count: form_data.lap_count || "",
    best_lap_time_ms: form_data.best_lap_time_ms || "",
    title: form_data.title || "",
    description: form_data.description || "",
    privacy: (form_data.privacy as PrivacyLevel) || "friends_only",
  });

  const [selectedSimulator, setSelectedSimulator] = useState(data.simulator);

  const filteredSimCars = selectedSimulator
    ? sim_cars.filter((car) => car.simulator === selectedSimulator)
    : sim_cars;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/event/create");
  };

  const formatLapTime = (value: string) => {
    // Allow digits, dots, and colons for lap time format like "1:23.456"
    const numeric = value.replace(/[^\d.:]/g, "");
    return numeric;
  };

  return (
    <>
      Modal or not modal?
      <Modal>
        <Head title="Create Event" />

        <Card className="w-full max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Create New Event</CardTitle>
            <CardDescription>
              Record your racing session details
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Messages */}
              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="text-sm text-red-600">
                    {errors.map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Event Title</Label>
                <Input
                  id="title"
                  value={data.title}
                  onChange={(e) => setData("title", e.target.value)}
                  placeholder="My awesome race session"
                />
                {formErrors.title && (
                  <p className="text-sm text-red-600">{formErrors.title}</p>
                )}
              </div>

              {/* Simulator */}
              <div className="space-y-2">
                <Label htmlFor="simulator">Simulator</Label>
                <select
                  id="simulator"
                  value={selectedSimulator}
                  onChange={(e) => {
                    setSelectedSimulator(e.target.value);
                    setData("simulator", e.target.value);
                    setData("sim_car_id", ""); // Reset car selection
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a simulator</option>
                  {simulators.map((sim) => (
                    <option key={sim.value} value={sim.value}>
                      {sim.label}
                    </option>
                  ))}
                </select>
                {formErrors.simulator && (
                  <p className="text-sm text-red-600">{formErrors.simulator}</p>
                )}
              </div>

              {/* Car */}
              <div className="space-y-2">
                <Label htmlFor="sim_car_id">Car</Label>
                <select
                  id="sim_car_id"
                  value={data.sim_car_id}
                  onChange={(e) => setData("sim_car_id", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!selectedSimulator}
                >
                  <option value="">Select a car</option>
                  {filteredSimCars.map((car) => (
                    <option key={car.id} value={car.id}>
                      {car.name} ({car.car_name})
                    </option>
                  ))}
                </select>
                {formErrors.sim_car_id && (
                  <p className="text-sm text-red-600">
                    {formErrors.sim_car_id}
                  </p>
                )}
              </div>

              {/* Track */}
              <div className="space-y-2">
                <Label htmlFor="track_id">Track</Label>
                <select
                  id="track_id"
                  value={data.track_id}
                  onChange={(e) => setData("track_id", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a track</option>
                  {tracks.map((track) => (
                    <option key={track.id} value={track.id}>
                      {track.name} ({track.country})
                    </option>
                  ))}
                </select>
                {formErrors.track_id && (
                  <p className="text-sm text-red-600">{formErrors.track_id}</p>
                )}
              </div>

              {/* Session Type */}
              <div className="space-y-2">
                <Label htmlFor="session_type">Session Type</Label>
                <select
                  id="session_type"
                  value={data.session_type}
                  onChange={(e) => setData("session_type", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select session type</option>
                  {session_types.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {formErrors.session_type && (
                  <p className="text-sm text-red-600">
                    {formErrors.session_type}
                  </p>
                )}
              </div>

              {/* Session Started At */}
              <div className="space-y-2">
                <Label htmlFor="session_started_at">Session Start Time</Label>
                <Input
                  id="session_started_at"
                  type="datetime-local"
                  value={data.session_started_at}
                  onChange={(e) =>
                    setData("session_started_at", e.target.value)
                  }
                />
                {formErrors.session_started_at && (
                  <p className="text-sm text-red-600">
                    {formErrors.session_started_at}
                  </p>
                )}
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label htmlFor="duration_seconds">Duration (seconds)</Label>
                <Input
                  id="duration_seconds"
                  type="number"
                  value={data.duration_seconds}
                  onChange={(e) => setData("duration_seconds", e.target.value)}
                  placeholder="3600"
                />
                {formErrors.duration_seconds && (
                  <p className="text-sm text-red-600">
                    {formErrors.duration_seconds}
                  </p>
                )}
              </div>

              {/* Lap Count */}
              <div className="space-y-2">
                <Label htmlFor="lap_count">Number of Laps</Label>
                <Input
                  id="lap_count"
                  type="number"
                  value={data.lap_count}
                  onChange={(e) => setData("lap_count", e.target.value)}
                  placeholder="20"
                />
                {formErrors.lap_count && (
                  <p className="text-sm text-red-600">{formErrors.lap_count}</p>
                )}
              </div>

              {/* Best Lap Time */}
              <div className="space-y-2">
                <Label htmlFor="best_lap_time_ms">
                  Best Lap Time (format: 1:23.456)
                </Label>
                <Input
                  id="best_lap_time_ms"
                  value={data.best_lap_time_ms}
                  onChange={(e) =>
                    setData("best_lap_time_ms", formatLapTime(e.target.value))
                  }
                  placeholder="1:23.456"
                />
                {formErrors.best_lap_time_ms && (
                  <p className="text-sm text-red-600">
                    {formErrors.best_lap_time_ms}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={data.description}
                  onChange={(e) => setData("description", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Optional notes about your session..."
                />
                {formErrors.description && (
                  <p className="text-sm text-red-600">
                    {formErrors.description}
                  </p>
                )}
              </div>

              {/* Privacy */}
              <div className="space-y-2">
                <PrivacySelect
                  label="Event Privacy"
                  description="Control who can view this event"
                  value={data.privacy}
                  onChange={(value) => setData("privacy", value)}
                />
                {formErrors.privacy && (
                  <p className="text-sm text-red-600">{formErrors.privacy}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <Button type="submit" disabled={processing}>
                  {processing ? "Creating..." : "Create Event"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </Modal>
    </>
  );
}
